import React, { useState, useEffect } from 'react';
import { ArrowLeft, User, Activity, AlertTriangle, Dna, GitBranch, FlaskConical, FileText, MessageSquare, Stethoscope, Phone, MapPin, Shield, Building, CreditCard, Building2, Plus } from 'lucide-react';
import patientService from '../services/patientService';
import PatientStudies from '../components/PatientStudies';
import AssignStudyModal from '../components/AssignStudyModal';
import PatientConsentsHistory from '../components/PatientConsentsHistory';
import MedicalNotes from '../components/MedicalNotes';
import PedigreeTree from '../components/PedigreeTree';
import PatientDocuments from '../components/PatientDocuments';
import '../components/PatientDetailRefactored.css'; // New dedicated styles

const PatientDetail = ({ patientId, onBack }) => {
    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('general');
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [refreshStudies, setRefreshStudies] = useState(0);

    useEffect(() => {
        const fetchPatient = async () => {
            try {
                setLoading(true);
                const data = await patientService.getById(patientId);
                setPatient(data);
            } catch (error) {
                console.error('Error fetching patient:', error);
            } finally {
                setLoading(false);
            }
        };

        if (patientId) {
            fetchPatient();
        }
    }, [patientId]);

    if (loading) return (
        <div className="patient-full-wrapper">
            <div className="loading-spinner">
                <div className="spinner"></div>
                <p>Cargando expediente...</p>
            </div>
        </div>
    );

    if (!patient) return (
        <div className="patient-full-wrapper">
            <div className="error-card">
                <p>Error al cargar el paciente</p>
                <button onClick={onBack} className="btn btn-secondary">
                    <ArrowLeft size={18} /> Volver
                </button>
            </div>
        </div>
    );

    const handleStudyAssigned = () => {
        setRefreshStudies(prev => prev + 1);
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'general':
                return (
                    <div className="detail-cards-stack">
                        {/* Datos Personales */}
                        <div className="detail-card">
                            <div className="card-header">
                                <div className="card-icon-title">
                                    <div className="icon-box">
                                        <User size={20} />
                                    </div>
                                    <h3 className="card-title">Datos Personales</h3>
                                </div>
                            </div>
                            <div className="detail-form-grid">
                                <div className="form-group">
                                    <label>Nombre Completo</label>
                                    <div className="detail-value-static">{patient.first_name} {patient.last_name} {patient.maternal_last_name || ''}</div>
                                </div>
                                {/* CURP oculto temporalmente — descomentar si se necesita en el futuro
                                <div className="form-group">
                                    <label>CURP</label>
                                    <div className="detail-value-static">{patient.curp || 'N/A'}</div>
                                </div>
                                */}
                                <div className="form-group">
                                    <label>Fecha de Nacimiento</label>
                                    <div className="detail-value-static">{patient.date_of_birth || 'N/A'}</div>
                                </div>
                                <div className="form-group">
                                    <label>Género</label>
                                    <div className="detail-value-static">{patient.gender || 'N/A'}</div>
                                </div>
                            </div>
                        </div>

                        {/* Referencia Médica */}
                        <div className="detail-card">
                            <div className="card-header">
                                <div className="card-icon-title">
                                    <div className="icon-box">
                                        <Stethoscope size={20} />
                                    </div>
                                    <h3 className="card-title">Referencia Médica</h3>
                                </div>
                            </div>
                            <div className="detail-form-grid">
                                <div className="form-group">
                                    <label>Médico Preferente</label>
                                    <div className="detail-value-static">{patient.preferred_doctor || 'N/A'}</div>
                                </div>
                                <div className="form-group">
                                    <label>Médico que Refiere</label>
                                    <div className="detail-value-static">{patient.referring_doctor_name || 'N/A'}</div>
                                </div>
                                <div className="form-group">
                                    <label>Especialidad (Referente)</label>
                                    <div className="detail-value-static">{patient.referring_doctor_specialty || 'N/A'}</div>
                                </div>
                                <div className="form-group">
                                    <label>Motivo de Consulta</label>
                                    <div className="detail-value-static">{patient.consultation_reason || 'N/A'}</div>
                                </div>
                            </div>
                        </div>

                        {/* Contacto */}
                        <div className="detail-card">
                            <div className="card-header">
                                <div className="card-icon-title">
                                    <div className="icon-box">
                                        <Phone size={20} />
                                    </div>
                                    <h3 className="card-title">Información de Contacto</h3>
                                </div>
                            </div>
                            <div className="detail-form-grid">
                                <div className="form-group">
                                    <label>Teléfono Principal</label>
                                    <div className="detail-value-static">{patient.phone || 'N/A'}</div>
                                </div>
                                <div className="form-group full-width">
                                    <label>Correo Electrónico</label>
                                    <div className="detail-value-static">{patient.email || 'N/A'}</div>
                                </div>
                            </div>
                        </div>

                        {/* Dirección */}
                        <div className="detail-card">
                            <div className="card-header">
                                <div className="card-icon-title">
                                    <div className="icon-box">
                                        <MapPin size={20} />
                                    </div>
                                    <h3 className="card-title">Domicilio</h3>
                                </div>
                            </div>
                            <div className="detail-form-grid">
                                <div className="form-group">
                                    <label>Calle y Número</label>
                                    <div className="detail-value-static">
                                        {patient.address_street ? `${patient.address_street} ${patient.address_number || ''}` : 'N/A'}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Colonia y Ciudad</label>
                                    <div className="detail-value-static">
                                        {patient.address_colony || patient.address_city ?
                                            `${patient.address_colony || ''}, ${patient.address_city || ''}` : 'N/A'
                                        }
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>Estado y CP</label>
                                    <div className="detail-value-static">
                                        {patient.address_state || patient.address_zip ?
                                            `${patient.address_state || ''} ${patient.address_zip ? `CP: ${patient.address_zip}` : ''}` : 'N/A'
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Datos Fiscales */}
                        {(patient.fiscal_rfc || patient.fiscal_razon_social) && (
                            <div className="detail-card">
                                <div className="card-header">
                                    <div className="card-icon-title">
                                        <div className="icon-box">
                                            <CreditCard size={20} />
                                        </div>
                                        <h3 className="card-title">Datos Fiscales</h3>
                                    </div>
                                </div>
                                <div className="detail-form-grid">
                                    <div className="form-group">
                                        <label>Razón Social</label>
                                        <div className="detail-value-static">{patient.fiscal_razon_social || patient.fiscal_name || 'N/A'}</div>
                                    </div>
                                    <div className="form-group">
                                        <label>RFC</label>
                                        <div className="detail-value-static">{patient.fiscal_rfc || 'N/A'}</div>
                                    </div>
                                    <div className="form-group">
                                        <label>Régimen Fiscal</label>
                                        <div className="detail-value-static">{patient.fiscal_regime || 'N/A'}</div>
                                    </div>
                                    <div className="form-group">
                                        <label>Uso de CFDI</label>
                                        <div className="detail-value-static">{patient.fiscal_uso_cfdi || 'N/A'}</div>
                                    </div>
                                    <div className="form-group full-width">
                                        <label>Dirección Fiscal</label>
                                        <div className="detail-value-static">
                                            {patient.fiscal_direccion ? `${patient.fiscal_direccion} ${patient.fiscal_zip || ''}` : 'N/A'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Seguro Médico */}
                        {(patient.is_insured === '1' || patient.is_insured === 1) && (
                            <div className="detail-card">
                                <div className="card-header">
                                    <div className="card-icon-title">
                                        <div className="icon-box">
                                            <Shield size={20} />
                                        </div>
                                        <h3 className="card-title">Seguro Médico</h3>
                                    </div>
                                </div>
                                <div className="detail-form-grid">
                                    <div className="form-group">
                                        <label>Seguro de Gastos Médicos</label>
                                        <div className="detail-value-static">{patient.insurance_provider || 'N/A'}</div>
                                    </div>
                                    <div className="form-group">
                                        <label>Número de Póliza</label>
                                        <div className="detail-value-static">{patient.policy_number || 'N/A'}</div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            case 'studies':
                return (
                    <PatientStudies
                        key={refreshStudies}
                        patientId={patientId}
                        patientName={`${patient.first_name} ${patient.last_name}${patient.maternal_last_name ? ' ' + patient.maternal_last_name : ''}`}
                        onAssignStudy={() => setShowAssignModal(true)}
                    />
                );
            case 'notes':
                return (
                    <div className="detail-cards-stack">
                        <MedicalNotes patientId={patientId} patientName={`${patient.first_name} ${patient.last_name}${patient.maternal_last_name ? ' ' + patient.maternal_last_name : ''}`} />
                    </div>
                );
            case 'pedigree':
                return <PedigreeTree patientId={patientId} />;
            case 'consents':
                return (
                    <div className="detail-card">
                        <PatientConsentsHistory patientId={patientId} />
                    </div>
                );
            case 'others':
                return (
                    <div className="detail-card">
                        <PatientDocuments patientId={patientId} />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className="patient-full-wrapper">
            <div className="patient-full-container">
                {/* Header Strip */}
                <div className="detail-header-strip">
                    <button onClick={onBack} className="btn-back-icon">
                        <ArrowLeft size={20} />
                    </button>
                    <div className="header-text">
                        <h2>{patient.first_name} {patient.last_name} {patient.maternal_last_name || ''}</h2>
                        <p>Expediente: {patient.patient_code || `#${patient.id}`} • {patient.age} años</p>
                    </div>
                </div>

                {/* Tabs Navigation */}
                <div className="tabs-navigation" style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button
                        className={`tab-button ${activeTab === 'general' ? 'active' : ''}`}
                        onClick={() => setActiveTab('general')}
                        style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', background: activeTab === 'general' ? '#083677' : 'white', color: activeTab === 'general' ? 'white' : '#64748b', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                    >
                        <User size={18} /> General
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'studies' ? 'active' : ''}`}
                        onClick={() => setActiveTab('studies')}
                        style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', background: activeTab === 'studies' ? '#083677' : 'white', color: activeTab === 'studies' ? 'white' : '#64748b', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                    >
                        <FlaskConical size={18} /> Estudios
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'notes' ? 'active' : ''}`}
                        onClick={() => setActiveTab('notes')}
                        style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', background: activeTab === 'notes' ? '#083677' : 'white', color: activeTab === 'notes' ? 'white' : '#64748b', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                    >
                        <Stethoscope size={18} /> Notas Médicas
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'pedigree' ? 'active' : ''}`}
                        onClick={() => setActiveTab('pedigree')}
                        style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', background: activeTab === 'pedigree' ? '#083677' : 'white', color: activeTab === 'pedigree' ? 'white' : '#64748b', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                    >
                        <GitBranch size={18} /> Árbol
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'consents' ? 'active' : ''}`}
                        onClick={() => setActiveTab('consents')}
                        style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', background: activeTab === 'consents' ? '#083677' : 'white', color: activeTab === 'consents' ? 'white' : '#64748b', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                    >
                        <FileText size={18} /> Consentimientos
                    </button>
                    <button
                        className={`tab-button ${activeTab === 'others' ? 'active' : ''}`}
                        onClick={() => setActiveTab('others')}
                        style={{ padding: '0.75rem 1.5rem', borderRadius: '8px', border: 'none', background: activeTab === 'others' ? '#083677' : 'white', color: activeTab === 'others' ? 'white' : '#64748b', fontWeight: '500', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                    >
                        <Plus size={18} /> Otros
                    </button>
                </div>

                <div className="tab-content">
                    {renderTabContent()}
                </div>
            </div>

            {showAssignModal && (
                <AssignStudyModal
                    patientId={patientId}
                    patientName={`${patient.first_name} ${patient.last_name}${patient.maternal_last_name ? ' ' + patient.maternal_last_name : ''}`}
                    onClose={() => setShowAssignModal(false)}
                    onSuccess={handleStudyAssigned}
                />
            )}
        </div>
    );
};

export default PatientDetail;


